import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import Loading from './components/Loading';
import User from './pages/User';
import { PrivateRoute } from './components/PrivateRoute';
import RegisterForm from './components/RegisterForm';
import LoginForm from './components/LoginForm';
import NotFound from './pages/NotFound';
import Setting from './pages/Setting';
import EditUser from './pages/User/EditUser';
import AddUser from './pages/User/AddUser';
import UserInfo from './pages/User/UserInfo';
import { API, getLogo, getSystemName, showError, showNotice } from './helpers';
import PasswordResetForm from './components/PasswordResetForm';
import GitHubOAuth from './components/GitHubOAuth';
import PasswordResetConfirm from './components/PasswordResetConfirm';
import { UserContext } from './context/User';
import { StatusContext } from './context/Status';
import Channel from './pages/Channel';
import Token from './pages/Token';
import EditToken from './pages/Token/EditToken';
import EditChannel from './pages/Channel/EditChannel';
import Redemption from './pages/Redemption';
import EditRedemption from './pages/Redemption/EditRedemption';
import TopUp from './pages/TopUp';
import Chat from './pages/Chat';
import LarkOAuth from './components/LarkOAuth';
import Git from './pages/Git';
import RepositoryBrowser from './pages/Repository';
import InviteCode from './pages/InviteCode';
import DataAnalytics from './pages/DataAnalytics';
import FeedbackManagement from './pages/Feedback';
import FeedbackSubmit from './pages/FeedbackSubmit';
import ScriptExampleManagement from './pages/ScriptExample';
import ExampleLibrary from './pages/ExampleLibrary';
import ExecutionBilling from './pages/ExecutionBilling';
import TokenUsage from './pages/TokenUsage';
import DynamicPrompt from './pages/DynamicPrompt';
import DynamicAPI from './pages/DynamicAPI';
import DataCollection from './pages/DataCollection';
import LogsUnified from './pages/LogsUnified';
import DataManagement from './pages/DataManagement';
import Action from './pages/Action';

const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));

function App() {
  const [userState, userDispatch] = useContext(UserContext);
  const [statusState, statusDispatch] = useContext(StatusContext);

  const loadUser = () => {
    let user = localStorage.getItem('user');
    if (user) {
      let data = JSON.parse(user);
      userDispatch({ type: 'login', payload: data });
    }
  };
  const loadStatus = async () => {
    const res = await API.get('/api/status');
    const { success, data } = res.data;
    if (success) {
      localStorage.setItem('status', JSON.stringify(data));
      statusDispatch({ type: 'set', payload: data });
      localStorage.setItem('system_name', data.system_name);
      localStorage.setItem('logo', data.logo);
      localStorage.setItem('footer_html', data.footer_html);
      localStorage.setItem('quota_per_unit', data.quota_per_unit);
      localStorage.setItem('display_in_currency', data.display_in_currency);
      if (data.chat_link) {
        localStorage.setItem('chat_link', data.chat_link);
      } else {
        localStorage.removeItem('chat_link');
      }
      if (
        data.version !== process.env.REACT_APP_VERSION &&
        data.version !== 'v0.0.0' &&
        process.env.REACT_APP_VERSION !== ''
      ) {
        showNotice(
          `新版本可用：${data.version}，请使用快捷键 Shift + F5 刷新页面`
        );
      }
    } else {
      showError('无法正常连接至服务器！');
    }
  };

  useEffect(() => {
    loadUser();
    loadStatus().then();
    let systemName = getSystemName();
    if (systemName) {
      document.title = systemName;
    }
    let logo = getLogo();
    if (logo) {
      let linkElement = document.querySelector("link[rel~='icon']");
      if (linkElement) {
        linkElement.href = logo;
      }
    }
  }, []);

  // 判断 Git 功能是否启用
  const gitEnabled = statusState?.status?.git_enabled !== false;

  return (
    <Routes>
      <Route
        path='/'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Home />
          </Suspense>
        }
      />
      <Route
        path='/channel'
        element={
          <PrivateRoute>
            <Channel />
          </PrivateRoute>
        }
      />
      <Route
        path='/channel/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditChannel />
          </Suspense>
        }
      />
      <Route
        path='/channel/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditChannel />
          </Suspense>
        }
      />
      <Route
        path='/token'
        element={
          <PrivateRoute>
            <Token />
          </PrivateRoute>
        }
      />
      <Route
        path='/token/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditToken />
          </Suspense>
        }
      />
      <Route
        path='/token/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditToken />
          </Suspense>
        }
      />
      <Route
        path='/redemption'
        element={
          <PrivateRoute>
            <Redemption />
          </PrivateRoute>
        }
      />
      <Route
        path='/redemption/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditRedemption />
          </Suspense>
        }
      />
      <Route
        path='/redemption/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditRedemption />
          </Suspense>
        }
      />
      <Route
        path='/user'
        element={
          <PrivateRoute>
            <User />
          </PrivateRoute>
        }
      />
      <Route
        path='/user/:id'
        element={
          <PrivateRoute>
            <UserInfo />
          </PrivateRoute>
        }
      />
      <Route
        path='/user/:id/edit'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditUser />
          </Suspense>
        }
      />
      <Route
        path='/user/edit/:id'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditUser />
          </Suspense>
        }
      />
      <Route
        path='/user/edit'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <EditUser />
          </Suspense>
        }
      />
      <Route
        path='/user/add'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <AddUser />
          </Suspense>
        }
      />
      <Route
        path='/invite_code'
        element={
          <PrivateRoute>
            <InviteCode />
          </PrivateRoute>
        }
      />
      <Route
        path='/action'
        element={
          <PrivateRoute>
            <Action />
          </PrivateRoute>
        }
      />
      <Route
        path='/analytics'
        element={
          <PrivateRoute>
            <DataAnalytics />
          </PrivateRoute>
        }
      />
                  <Route
                    path='/data-collection'
                    element={
                      <PrivateRoute>
                        <DataCollection />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/feedback'
                    element={
                      <PrivateRoute>
                        <FeedbackManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/feedback/submit'
                    element={
                      <PrivateRoute>
                        <FeedbackSubmit />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/script-examples'
                    element={
                      <PrivateRoute>
                        <ScriptExampleManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/examples'
                    element={
                      <PrivateRoute>
                        <ExampleLibrary />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/execution-billing'
                    element={
                      <PrivateRoute>
                        <ExecutionBilling />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/token-usage'
                    element={
                      <PrivateRoute>
                        <TokenUsage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/dynamic-prompts'
                    element={
                      <PrivateRoute>
                        <DynamicPrompt />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/dynamic-apis'
                    element={
                      <PrivateRoute>
                        <DynamicAPI />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/logs'
                    element={
                      <PrivateRoute>
                        <LogsUnified />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path='/data-management'
                    element={
                      <PrivateRoute>
                        <DataManagement />
                      </PrivateRoute>
                    }
                  />
      <Route
        path='/user/reset'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <PasswordResetConfirm />
          </Suspense>
        }
      />
      <Route
        path='/login'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LoginForm />
          </Suspense>
        }
      />
      <Route
        path='/register'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <RegisterForm />
          </Suspense>
        }
      />
      <Route
        path='/reset'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <PasswordResetForm />
          </Suspense>
        }
      />
      <Route
        path='/oauth/github'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <GitHubOAuth />
          </Suspense>
        }
      />
      <Route
        path='/oauth/lark'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LarkOAuth />
          </Suspense>
        }
      />
      <Route
        path='/setting'
        element={
          <PrivateRoute>
            <Suspense fallback={<Loading></Loading>}>
              <Setting />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path='/topup'
        element={
        <PrivateRoute>
          <Suspense fallback={<Loading></Loading>}>
            <TopUp />
          </Suspense>
          </PrivateRoute>
        }
      />
      {gitEnabled && (
        <>
          <Route
            path='/git'
            element={
              <PrivateRoute>
                <Git />
              </PrivateRoute>
            }
          />
          <Route
            path='/repository/:namespace/:name'
            element={
              <PrivateRoute>
                <RepositoryBrowser />
              </PrivateRoute>
            }
          />
        </>
      )}
      <Route
        path='/about'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <About />
          </Suspense>
        }
      />
      <Route
        path='/chat'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <Chat />
          </Suspense>
        }
      />
      <Route path='*' element={
          <NotFound />
      } />
    </Routes>
  );
}

export default App;
